import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { doc, onSnapshot, updateDoc, setDoc, query, where, getDocs, writeBatch, collection, getDoc } from 'firebase/firestore';
import { TEACHER_UID } from './AuthComponents';
import { NotificationBell } from './NotificationComponents';

// Profile Edit Modal Component
export const ProfileEditModal = ({
  showProfileEdit,
  setShowProfileEdit,
  profileForm,
  setProfileForm,
  handleUpdateProfile,
  userRole,
  currentUserUid
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopyStudentId = async () => {
    if (!currentUserUid) return;
    try {
      await navigator.clipboard.writeText(currentUserUid);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy Student ID:', err);
    }
  };
  if (!showProfileEdit) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Edit Your Profile</h2>
        <label>First Name:</label>
        <input
          type="text"
          value={profileForm.firstName}
          onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
        />
        <label>Last Name:</label>
        <input
          type="text"
          value={profileForm.lastName}
          onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
        />
        {userRole !== 'parent' && (
          <>
            <label>Grade:</label>
            <select
              value={profileForm.grade}
              onChange={(e) => setProfileForm({ ...profileForm, grade: e.target.value })}
              className="profile-grade-dropdown"
            >
              <option value="">Select Grade</option>
              <option value="8">8th Grade</option>
              <option value="9">9th Grade</option>
              <option value="10">10th Grade</option>
              <option value="11">11th Grade</option>
              <option value="12">12th Grade</option>
              <option value="Portfolio Defense">Portfolio Defense</option>
            </select>
            <label>Profile Picture URL:</label>
            <input
              type="text"
              value={profileForm.profilePicUrl}
              onChange={(e) => setProfileForm({ ...profileForm, profilePicUrl: e.target.value })}
            />
          </>
        )}
        {/* Show Firebase ID for students (read-only) */}
        {userRole === 'student' && (
          <div className="student-id-row">
            <label>Your Student ID:</label>
            <div className="student-id-copy">
              <span className="mono-id" title={currentUserUid || ''}>{currentUserUid || ''}</span>
              <button
                type="button"
                className="copy-button"
                onClick={handleCopyStudentId}
                aria-live="polite"
                aria-label={copied ? 'Copied' : 'Copy student ID to clipboard'}
                title={copied ? 'Copied!' : 'Copy'}
              >
                <i className="fa-solid fa-copy" aria-hidden="true"></i>
              </button>
              {copied && <span className="copy-toast">Copied!</span>}
            </div>
          </div>
        )}
        <button onClick={handleUpdateProfile}>Save Changes</button>
        <button onClick={() => setShowProfileEdit(false)}>Cancel</button>
      </div>
    </div>
  );
};

// User Info Header Component
export const UserInfoHeader = ({
  userProfile,
  currentUser,
  currentUserRole,
  pendingFeedbackNotifications,
  pendingProposalNotifications,
  pendingMentorRequestNotifications,
  pendingLorRequestNotifications,
  pendingTranscriptRequestNotifications,
  pendingProjectProposalNotifications,
  showNotificationDropdown,
  setShowNotificationDropdown,
  handleNotificationClick,
  setShowProfileEdit,
  handleLogout
}) => {
  return (
    <header className="header">
      <h1>Barefoot Technology Academy</h1>
      <div className="user-info">
        {userProfile.profilePicUrl && (
          <img src={userProfile.profilePicUrl} alt="Profile" className="profile-pic" />
        )}
        <span>{userProfile.firstName || userProfile.name || currentUser.email} ({currentUserRole})</span>
        
        {/* Notification Bell for Teachers */}
        {userProfile?.role === 'teacher' && (
          <NotificationBell 
            pendingFeedbackNotifications={pendingFeedbackNotifications}
            pendingProposalNotifications={pendingProposalNotifications}
            pendingMentorRequestNotifications={pendingMentorRequestNotifications}
            pendingLorRequestNotifications={pendingLorRequestNotifications}
            pendingTranscriptRequestNotifications={pendingTranscriptRequestNotifications}
            pendingProjectProposalNotifications={pendingProjectProposalNotifications}
            showNotificationDropdown={showNotificationDropdown}
            setShowNotificationDropdown={setShowNotificationDropdown}
          />
        )}
        
        <button onClick={() => setShowProfileEdit(true)}>Edit Profile</button>
        <button onClick={handleLogout}>Logout</button>
      </div>
    </header>
  );
};

// Profile Management Hook
export const useProfileManagement = ({ currentUser }) => {
  const [userProfile, setUserProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ firstName: '', lastName: '', grade: '', profilePicUrl: '' });
  const [showProfileEdit, setShowProfileEdit] = useState(false);

  // Listen for User Profile
  useEffect(() => {
    if (!currentUser) {
      setUserProfile(null);
      return;
    }

    const userProfileRef = doc(db, 'users', currentUser.uid);
    
    // Helper function to set default profile
    const setDefaultProfile = () => {
      const defaultProfile = {
        firstName: '',
        lastName: '',
        name: currentUser.email, // Keep name for backward compatibility
        grade: '',
        profilePicUrl: '',
        role: currentUser.uid === TEACHER_UID ? 'teacher' : 'student'
      };
      // Set profile immediately so app doesn't hang, then try to create in Firestore
      setUserProfile(defaultProfile);
      setProfileForm(defaultProfile);
      
      // Try to create the profile in Firestore (non-blocking)
      setDoc(userProfileRef, defaultProfile, { merge: true })
        .catch(error => console.error("Error creating default user profile:", error));
    };

    // Try onSnapshot first (real-time updates)
    let unsubscribeUserProfile;
    try {
      unsubscribeUserProfile = onSnapshot(
        userProfileRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setUserProfile(data);
            // Initialize profile form with fetched data
            setProfileForm({
              firstName: data.firstName || '',
              lastName: data.lastName || '',
              grade: data.grade || '',
              profilePicUrl: data.profilePicUrl || ''
            });
          } else {
            console.warn("User profile not found for UID:", currentUser.uid, "Setting default profile.");
            setDefaultProfile();
          }
        },
        (error) => {
          console.error("Error in onSnapshot for user profile:", error);
          // Fallback to getDoc if onSnapshot fails
          getDoc(userProfileRef)
            .then((docSnap) => {
              if (docSnap.exists()) {
                const data = docSnap.data();
                setUserProfile(data);
                setProfileForm({
                  firstName: data.firstName || '',
                  lastName: data.lastName || '',
                  grade: data.grade || '',
                  profilePicUrl: data.profilePicUrl || ''
                });
              } else {
                setDefaultProfile();
              }
            })
            .catch((getDocError) => {
              console.error("Error in getDoc fallback:", getDocError);
              // Set default profile even if both fail, so app doesn't hang
              setDefaultProfile();
            });
        }
      );
    } catch (error) {
      console.error("Error setting up onSnapshot:", error);
      // Fallback to getDoc if onSnapshot setup fails
      getDoc(userProfileRef)
        .then((docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setUserProfile(data);
            setProfileForm({
              firstName: data.firstName || '',
              lastName: data.lastName || '',
              grade: data.grade || '',
              profilePicUrl: data.profilePicUrl || ''
            });
          } else {
            setDefaultProfile();
          }
        })
        .catch((getDocError) => {
          console.error("Error in getDoc fallback:", getDocError);
          setDefaultProfile();
        });
    }

    return () => {
      if (unsubscribeUserProfile) {
        unsubscribeUserProfile();
      }
    };
  }, [currentUser]);

  const handleUpdateProfile = async () => {
    if (!currentUser) return;
    
    // Get the old username for comparison
    const oldUsername = userProfile?.name;
    const newUsername = `${profileForm.firstName} ${profileForm.lastName}`.trim() || currentUser.email;
    
    try {
      // Update the user profile first
      if (userProfile?.role === 'parent') {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          firstName: profileForm.firstName,
          lastName: profileForm.lastName,
          name: `${profileForm.firstName} ${profileForm.lastName}`.trim() || currentUser.email
        });
      } else {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          firstName: profileForm.firstName,
          lastName: profileForm.lastName,
          name: `${profileForm.firstName} ${profileForm.lastName}`.trim() || currentUser.email, // Update name for backward compatibility
          grade: profileForm.grade,
          profilePicUrl: profileForm.profilePicUrl
        });
      }

      // If the username changed, update all related records
      if (oldUsername !== newUsername && newUsername.trim()) {
        console.log(`Username changed from "${oldUsername}" to "${newUsername}". Updating all records...`);
        
        // Update posts, comments, and tag feedback in parallel
        await Promise.all([
          updateAllPostsWithNewUsername(currentUser.uid, newUsername),
          updateAllCommentsWithNewUsername(currentUser.uid, newUsername),
          // Only update tag feedback if the user is a teacher
          userProfile?.role === 'teacher' ? updateAllTagFeedbackWithNewTeacherName(currentUser.uid, newUsername) : Promise.resolve()
        ]);
        
        console.log('All username updates completed successfully');
      }
      
      // alert('Profile updated successfully!');
      setShowProfileEdit(false);
    } catch (error) {
      console.error("Error updating profile:", error.message);
      alert('Failed to update profile. Please try again.');
    }
  };

  return {
    userProfile,
    setUserProfile,
    profileForm,
    setProfileForm,
    showProfileEdit,
    setShowProfileEdit,
    handleUpdateProfile
  };
};

// Utility Functions for Username Updates
export const profileUtils = {
  updateAllPostsWithNewUsername: async (userId, newUsername) => {
    try {
      // Query all posts by this user
      const postsQuery = query(collection(db, 'posts'), where('userId', '==', userId));
      const postsSnapshot = await getDocs(postsQuery);
      
      if (postsSnapshot.empty) {
        console.log('No posts found for user:', userId);
        return;
      }

      // Batch update all posts
      const batch = writeBatch(db);
      postsSnapshot.docs.forEach(doc => {
        batch.update(doc.ref, { username: newUsername });
      });
      
      await batch.commit();
      console.log(`Updated ${postsSnapshot.docs.length} posts with new username: ${newUsername}`);
    } catch (error) {
      console.error('Error updating posts with new username:', error);
      throw error;
    }
  },

  updateAllCommentsWithNewUsername: async (userId, newUsername) => {
    try {
      // Query all comments by this user
      const commentsQuery = query(collection(db, 'comments'), where('userId', '==', userId));
      const commentsSnapshot = await getDocs(commentsQuery);
      
      if (commentsSnapshot.empty) {
        console.log('No comments found for user:', userId);
        return;
      }

      // Batch update all comments
      const batch = writeBatch(db);
      commentsSnapshot.docs.forEach(doc => {
        batch.update(doc.ref, { username: newUsername });
      });
      
      await batch.commit();
      console.log(`Updated ${commentsSnapshot.docs.length} comments with new username: ${newUsername}`);
    } catch (error) {
      console.error('Error updating comments with new username:', error);
      throw error;
    }
  },

  updateAllTagFeedbackWithNewTeacherName: async (teacherId, newTeacherName) => {
    try {
      // Query all tag feedback by this teacher
      const tagFeedbackQuery = query(collection(db, 'tagFeedback'), where('teacherId', '==', teacherId));
      const tagFeedbackSnapshot = await getDocs(tagFeedbackQuery);
      
      if (tagFeedbackSnapshot.empty) {
        console.log('No tag feedback found for teacher:', teacherId);
        return;
      }

      // Batch update all tag feedback
      const batch = writeBatch(db);
      tagFeedbackSnapshot.docs.forEach(doc => {
        batch.update(doc.ref, { teacherName: newTeacherName });
      });
      
      await batch.commit();
      console.log(`Updated ${tagFeedbackSnapshot.docs.length} tag feedback entries with new teacher name: ${newTeacherName}`);
    } catch (error) {
      console.error('Error updating tag feedback with new teacher name:', error);
      throw error;
    }
  }
};

// Import the functions for use in the hook
const { updateAllPostsWithNewUsername, updateAllCommentsWithNewUsername, updateAllTagFeedbackWithNewTeacherName } = profileUtils;

const profileComponents = {
  ProfileEditModal,
  UserInfoHeader,
  useProfileManagement,
  profileUtils
};

export default profileComponents;
